import { Component, HostBinding, OnChanges, OnInit, ElementRef, ViewChild } from '@angular/core';
import { AnalyticsSettingsEvent } from 'analytics-api';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { AnalyticsComponentEvent } from 'idt-analytics-api/models';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';

import { IdtButtonStylesSettings, PieceButtonSettings } from './models/piece-button.models';
import { getWysiwygAlignmentClasses } from './piece-button-utils';


@Component({
  selector: 'piece-button-render',
  templateUrl: './piece-button-render.component.html',
  styleUrls: ['./piece-button-render.component.scss'],
})
export class PieceButtonRenderComponent
  extends BaseRenderComponent<PieceButtonSettings>
  implements OnChanges, OnInit {

  @ViewChild('image') public image?: ElementRef<HTMLDivElement>;

  @HostBinding('class') public aligmentClasses: string | undefined;

  public orientation$: Observable<string>;
  public style: IdtButtonStylesSettings = {
    backgroundColor: '',
    hoverBackgroundColor: '',
    hoverColor: '',
    pressedColor: '',
    pressedBackgroundColor: '',
    imageToTextPosition: 'up'
  };

  public analyticsEventClickButton: string = '';
  public analyticsEventData: AnalyticsComponentEvent = {};

  public hover = false;
  public active = false;

  constructor(
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly productService: ProductsService
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  public ngOnInit() {
    if (isDefined(this.settings.analytics)) {
      this.setAnalyticsEvents();
    }
  }

  public ngOnChanges() {
    if (this.piece) {
      this.aligmentClasses = getWysiwygAlignmentClasses(this.piece);
    }

    const buttonStyles = this.settings.general.buttonStyles;

    if (!!buttonStyles.backgroundColor) {
      this.style.backgroundColor = buttonStyles.backgroundColor;
    }

    if (!!buttonStyles.hoverBackgroundColor) {
      this.style.hoverBackgroundColor = buttonStyles.hoverBackgroundColor;
    } else if (!!buttonStyles.backgroundColor) {
      this.style.hoverBackgroundColor = buttonStyles.backgroundColor;
    }

    if (!!buttonStyles.hoverColor) {
      this.style.hoverColor = buttonStyles.hoverColor;
    }

    if (!!buttonStyles.pressedColor) {
      this.style.pressedColor = buttonStyles.pressedColor;
    }

    if (!!buttonStyles.pressedBackgroundColor) {
      this.style.pressedBackgroundColor = buttonStyles.pressedBackgroundColor;
    } else if (!!buttonStyles.backgroundColor) {
      this.style.pressedBackgroundColor = buttonStyles.backgroundColor;
    }
  }

  public getPortraitImageSrc() {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef)
    };
    return this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions);
  }

  public getLandscapeImageSrc() {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef)
    };
    return this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions);
  }

  public get mergedStyles(): Partial<IdtButtonStylesSettings> & Partial<PieceButtonSettings> {
    if (!isDefined(this.piece) || !isDefined(this.piece.settings)
      || !isDefined(this.piece.settings.styles)
      || !isDefined(this.piece.settings.general)) {
      return {};
    }

    return {
      ...this.piece.settings.styles,
      ...this.piece.settings.general.buttonStyles,
    };
  }

  public buttonHolderMouseIn() {
    if (!this.hover && this.piece?.settings?.general.buttonStyles.imageToTextPosition === 'hover' && this.image) {
      this.image.nativeElement.style.display = 'block';
      this.image.nativeElement.style.pointerEvents = 'none';
      document.addEventListener('pointermove', this.imageFollowMouse);
    }
    this.hover = true;
  }

  public buttonHolderMouseOut(): void {
    if (this.piece?.settings?.general.buttonStyles.imageToTextPosition === 'hover' && this.image) {
      this.image.nativeElement.style.display = '';
      this.image.nativeElement.style.pointerEvents = '';
      document.removeEventListener('pointermove', this.imageFollowMouse);
    }
    this.hover = false;
  }

  public buttonHolderMouseDown(): void {
    this.active = true;
    document.addEventListener('mouseup', this.buttonHolderMouseUp);
  }

  public buttonHolderMouseUp = () => {
    this.active = false;
    document.removeEventListener('mouseup', this.buttonHolderMouseUp);
  };

  public settingsStyleButtonHolder() {
    const style: { backgroundColor: string } = { backgroundColor: this.style.backgroundColor! };
    if (this.hover && this.style.hoverBackgroundColor) {
      style.backgroundColor = this.style.hoverBackgroundColor;
    }
    if (this.active && this.style.pressedBackgroundColor) {
      style.backgroundColor = this.style.pressedBackgroundColor;
    }
    return style;
  }

  public settingsStyleWysiwyg() {
    const style: { color?: string } = { color: '' };
    if (this.hover && this.style.hoverColor) {
      style.color = this.style.hoverColor;
    }
    if (this.active && this.style.pressedColor) {
      style.color = this.style.pressedColor;
    }
    return style;
  }

  public setAnalyticsEvents() {
    const eventClickButtonInfo = this.settings.analytics?.events.find((ev: AnalyticsSettingsEvent) => ev.name === 'clickButton');
    this.analyticsEventClickButton = eventClickButtonInfo?.relatedEvent || '';

    let text: string | undefined;
    if (this.settings.general.text) {
      text = this.settings.general.text.replace(/(<([^>]+)>)/ig, '');
    } else if (this.settings.general.alt) {
      text = this.settings.general.alt;
    }
    this.analyticsEventData = {
      buttonText: text
    };
  }

  public onRenderLinkClick(productInfo: { productId: string | undefined; categoryId: string | undefined }): void {
    if (productInfo.productId) {
      this.productService.productDetail(productInfo.productId, productInfo.categoryId).pipe(
        take(1)
      ).subscribe((product) => {
        const cmsEvent: CMSEvent<Product> = {
          name: 'productLink',
          type: 'click',
          data: product
        };
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      });
    }
  }

  private readonly imageFollowMouse = (event: PointerEvent): void => {
    const imageDimensions = this.image!.nativeElement.getBoundingClientRect();
    const posX = event.offsetX - (imageDimensions.width / 2);
    const posY = event.offsetY - (imageDimensions.height / 2);

    this.image!.nativeElement.style.left = `${posX}px`;
    this.image!.nativeElement.style.top = `${posY}px`;
  };

}
